import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useFormGuardActions } from '@/entities/formGuard';
import { type KmEntity, type KmEntityUpdate, useUpdateKmEntity } from '@/entities/ontology';
import { createPredicateSchema, type PredicateValue, toPredicatePayload } from '@/entities/predicate';
import { isKmFormFieldDirty } from './attrs/attrFormValue';
import {
  checkKmAttrRows,
  collectChangedKmAttrValues,
  type KmAttrRowValue,
  kmAttrRowsSchema,
  useKmEntityAttrsForm,
} from './attrs/useKmEntityAttrsForm';

/** Значения формы карточки: условие применимости и значения атрибутов экземпляра */
export type KmEntityCardFormValues = {
  predicate?: PredicateValue | null;
  rows: KmAttrRowValue[];
};

/**
 * Форма карточки экземпляра: все её блоки правятся вместе и сохраняются одним запросом —
 * в него попадает только изменившееся. Пока правки не сохранены, карточка держит признак
 * изменений, которым страница защищается от их потери.
 */
export const useKmEntityCardForm = (entity: KmEntity) => {
  const { t } = useTranslation(['kmEntities', 'common']);
  const { setIsDirty } = useFormGuardActions();

  const { schemaRows, extraRows, rowValues, resolveAttrType } = useKmEntityAttrsForm(entity);
  const { mutate: updateEntity, isPending: isSaving, error: saveError } = useUpdateKmEntity();

  const schema = useMemo(
    () =>
      z
        .object({
          predicate: createPredicateSchema(t('common:predicateHasIssues')).optional().nullable(),
          rows: kmAttrRowsSchema,
        })
        .superRefine((values, ctx) => checkKmAttrRows(schemaRows, values.rows, resolveAttrType, t, ctx)),
    [schemaRows, resolveAttrType, t],
  );

  const values = useMemo<KmEntityCardFormValues>(
    () => ({
      predicate: { dsl: entity.predicate?.dsl ?? '', dslHuman: entity.predicate?.dslHuman ?? '' },
      rows: rowValues,
    }),
    [entity.predicate, rowValues],
  );

  const form = useForm<KmEntityCardFormValues>({
    values,
    resolver: zodResolver(schema),
    // Перечитанная сущность не затирает несохранённую правку соседнего блока
    resetOptions: { keepDirtyValues: true },
  });

  const {
    formState: { dirtyFields, isDirty },
  } = form;

  useEffect(() => {
    setIsDirty(isDirty);
    // Уход с карточки признак снимает: он относится к её правкам, а не к разделу
    return () => setIsDirty(false);
  }, [isDirty, setIsDirty]);

  const onSubmit = form.handleSubmit((formValues) => {
    const payload: KmEntityUpdate = {};

    const changedData = collectChangedKmAttrValues(schemaRows, formValues.rows, dirtyFields.rows, resolveAttrType);
    if (Object.keys(changedData).length) {
      // Слияние: значения с кодами вне схемы типа остаются у экземпляра
      payload.data = changedData;
      payload.mode = 'merge';
    }

    if (isKmFormFieldDirty(dirtyFields.predicate)) {
      payload.predicate = toPredicatePayload(formValues.predicate);
    }

    if (!Object.keys(payload).length) {
      return;
    }

    updateEntity(
      { id: entity.id, payload },
      // Сохранённое становится исходным: следующее сохранение снова уйдёт только по правкам.
      // Отказ поля не трогает — введённое остаётся на месте
      { onSuccess: () => form.reset(formValues) },
    );
  });

  return { form, schemaRows, extraRows, resolveAttrType, onSubmit, isSaving, isDirty, saveError } as const;
};
